// The NEEDS-ATTENTION TRAY (spec 06 §9.2: "parks the entry in a 'needs
// attention' tray on the device").
//
// This is the screen that makes the outbox honest. A queue that silently
// swallows a refusal is worse than no queue: the operator believes 1 300 m was
// cut, the ledger says otherwise, and nobody finds out until a count. So every
// item the queue could not apply lands here, named in the operator's own terms
// ("Cut · 2 pieces"), with the server's own reason, and with exactly the
// affordances that reason supports:
//
//   RETRY   — the same bytes can still succeed (an aged-out 5xx, a 404 whose
//             predecessor finally landed, a permission that was just granted).
//             The ORIGINAL client key is re-sent, so a retry of something the
//             server already applied is a replay, not a second application.
//   RE-PLAN — the world moved (§9.1's fresh remaining, a count that closed, a
//             transfer already received). Retrying the same numbers is
//             meaningless; the operator opens the entity and redoes the work.
//             The fresh figure the problem carried is printed right here so the
//             re-plan starts from a fact.
//   DISCARD — always available, always confirmed, and the confirmation says the
//             thing that is easy to get wrong: discarding removes the QUEUED
//             attempt and undoes nothing the server already did.
//
// There is deliberately no "retry all" button. Each parked item failed for its
// own reason and a bulk retry is a bulk guess.
//
// Since 04-B-3 the tray carries a SECOND section (D-080): production events the
// server answered 200 for and REFUSED into `floor_sync_conflicts`. Those are
// not parked sends — the fact is already in the office's conflict queue (S7,
// /production/conflicts) and re-sending would only dedupe — so the row offers
// neither Retry nor Open, only an explicit confirmed dismiss that says who
// resolves it: the office. Rendering them as successes was the silent-loss bug
// the D-080 settlement contract exists to prevent; rendering them as retryable
// failures would invite double work.

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FloorButton, FloorEmpty, FloorScreen } from "../components/floor-chrome";
import { floorOutbox, useFloorOutbox } from "../hooks/use-floor-outbox";
import { itemLabel, itemTarget, parkedAdvice, type OutboxItem } from "../lib/floor-outbox";
import { snapshotAgeParts } from "../lib/floor-read-cache";
import { floorFailureDetail, floorFailureMessage } from "../lib/floor-errors";

export function FloorTrayScreen() {
  const { t } = useTranslation();
  const outbox = useFloorOutbox();
  const queue = floorOutbox();
  const [confirming, setConfirming] = useState<string | undefined>(undefined);

  return (
    <FloorScreen
      title={t("inventory.floor.tray.title")}
      back={
        <Link to="/floor" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      {/* WAITING TO SEND — THE LIST, NOT THE NUMBER (D-121, UX-5 surface
          review). This screen counted the queue and never named it: "4 more are
          still waiting to send", on the one screen an operator opens BECAUSE
          they are unsure whether their work landed. A number cannot answer the
          question they came with — WHICH of my taps is still on the device? —
          and the two ways of being wrong about it are not symmetric. Believing a
          tap is missing when it is queued makes the operator do it again, and a
          re-tap is a SECOND event in an append-only ledger: two 420 m reports
          against one drum, found by a supervisor at a count, days later.
          Believing a tap landed when it did not is caught by the tray's other
          two sections, which name their items one by one — the queue was the
          only class of held work the screen would not show.

          Every fact the row needs was already on the item: `identity` is the
          physical thing in the operator's own words, captured at tap time for
          exactly this purpose, and `createdAt` is when they did it. The parked
          and conflicted rows have printed both since 04-B-3. */}
      {outbox.pendingCount > 0 ? (
        <section className="floor-panel" data-testid="floor-tray-pending">
          <h2 className="floor-panel-head">{t("inventory.floor.tray.pendingHead")}</h2>
          <p className="floor-hint" data-testid="tray-pending-note">
            {t("inventory.floor.tray.pendingNote", { count: outbox.pendingCount })}
          </p>
          <p className="floor-hint">{t("inventory.floor.tray.pendingBody")}</p>
          {/* The COUNT gates the section and the ITEMS fill the list, on
              purpose. They come off the same snapshot and cannot normally
              disagree; if a future change ever makes them, the operator still
              gets the sentence that says work is held, rather than a screen
              that silently loses the whole section because a list was empty. */}
          <ul className="floor-session" data-testid="floor-tray-pending-list">
            {outbox.pending.map((item) => (
              <li key={item.clientKey} className="floor-session-row" data-testid="tray-pending-row">
                <TrayIdentity item={item} />
                <span className="floor-tray-when">
                  <TrayWhen at={item.createdAt} />
                </span>
                {/* The head of a FIFO queue waiting out a backoff is the one
                    item whose delay has a cause the operator can see. Naming it
                    is the difference between "the queue is slow" and "this one
                    is being retried", which is what decides whether they wait. */}
                {outbox.blockedBy?.clientKey === item.clientKey ? (
                  <span className="floor-tray-advice" data-testid="tray-pending-blocked">
                    {t("inventory.floor.tray.pendingBlocked")}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Queued by SOMEONE ELSE. Held rather than sent, because draining it
          under this session would stamp another operator's cut with this user
          in the ledger and in the append-only audit chain. */}
      {outbox.foreignCount > 0 ? (
        <section className="floor-panel" data-testid="floor-tray-foreign">
          <h2 className="floor-panel-head">{t("inventory.floor.tray.otherUserHead")}</h2>
          <p className="floor-hint">
            {t("inventory.floor.tray.otherUserBody", { count: outbox.foreignCount })}
          </p>
          <ul className="floor-session">
            {outbox.foreign.map((item) => (
              <li key={item.clientKey} className="floor-session-row">
                <TrayIdentity item={item} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Server-conflicted production events (D-080). The event is PERSISTED
          office-side in floor_sync_conflicts — nothing here is lost, nothing
          here is retryable; the only device action is dismissing the note. */}
      {outbox.reviewCount > 0 ? (
        <section className="floor-panel" data-testid="floor-tray-review">
          <h2 className="floor-panel-head">{t("inventory.floor.tray.reviewHead")}</h2>
          <p className="floor-hint">{t("inventory.floor.tray.reviewBody")}</p>
          <ul className="floor-tray">
            {outbox.needsReview.map((item) => (
              <ReviewRow
                key={item.clientKey}
                item={item}
                confirming={confirming === item.clientKey}
                onConfirmDismiss={() => {
                  setConfirming(item.clientKey);
                }}
                onCancelDismiss={() => {
                  setConfirming(undefined);
                }}
                onDismiss={() => {
                  setConfirming(undefined);
                  void queue.discard(item.clientKey);
                }}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {outbox.attentionCount === 0 && outbox.reviewCount === 0 ? (
        <FloorEmpty
          title={t("inventory.floor.tray.emptyTitle")}
          body={
            outbox.pendingCount > 0
              ? t("inventory.floor.tray.emptyBodyPending")
              : t("inventory.floor.tray.emptyBody")
          }
        />
      ) : outbox.attentionCount === 0 ? null : (
        <ul className="floor-tray" data-testid="floor-tray">
          {outbox.needsAttention.map((item) => (
            <TrayRow
              key={item.clientKey}
              item={item}
              confirming={confirming === item.clientKey}
              onConfirmDiscard={() => {
                setConfirming(item.clientKey);
              }}
              onCancelDiscard={() => {
                setConfirming(undefined);
              }}
              onDiscard={() => {
                setConfirming(undefined);
                void queue.discard(item.clientKey);
              }}
              onRetry={() => {
                void queue.retry(item.clientKey);
              }}
            />
          ))}
        </ul>
      )}
    </FloorScreen>
  );
}

function TrayRow({
  item,
  confirming,
  onConfirmDiscard,
  onCancelDiscard,
  onDiscard,
  onRetry,
}: {
  item: OutboxItem;
  confirming: boolean;
  onConfirmDiscard: () => void;
  onCancelDiscard: () => void;
  onDiscard: () => void;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation();
  const label = itemLabel(item);
  const target = itemTarget(item);
  const failure = item.lastError;
  const advice = failure === undefined ? "retry" : parkedAdvice(failure);
  const detail = failure === undefined ? undefined : floorFailureDetail(t, failure, i18n.language);

  return (
    <li className="floor-tray-row" data-testid="floor-tray-row" data-advice={advice}>
      {/* WHICH BOBBIN. Three rows reading "Cut · 2 pieces / Tried 3 times" plus
          a request UUID are not something an operator can act on; the physical
          identity and the time they did the work are what makes the row a task
          rather than a puzzle. */}
      <p className="floor-tray-op" data-testid="floor-tray-op">
        {t(label.key, label.params)}
        {item.identity !== undefined ? ` · ${item.identity}` : ""}
      </p>
      <p className="floor-tray-when" data-testid="floor-tray-when">
        <TrayWhen at={item.createdAt} />
      </p>

      <p className="floor-tray-reason" data-testid="floor-tray-reason">
        {failure === undefined
          ? t("inventory.floor.tray.unknownReason")
          : floorFailureMessage(t, failure)}
      </p>

      {detail !== undefined ? (
        <p className="floor-tray-detail" data-testid="floor-tray-detail">
          {detail}
        </p>
      ) : null}

      {/* div, not p: the support disclosure nests a <details> block, which is
          invalid inside a paragraph (browsers reparse it out). */}
      <div className="floor-tray-meta">
        {t("inventory.floor.tray.attempts", { count: item.attempts })}
        {/* The request id is for support, not for the aisle — kept, but behind
            a disclosure so the operator-facing row stays readable. */}
        {failure?.requestId !== undefined ? (
          <details className="floor-tray-support">
            <summary>{t("inventory.floor.tray.supportDetails")}</summary>
            <code>{failure.requestId}</code>
          </details>
        ) : null}
      </div>

      {advice === "futile" ? (
        <p className="floor-tray-advice" data-testid="floor-tray-futile">
          {t("inventory.floor.tray.adviceFutile")}
        </p>
      ) : null}
      {advice === "replan" ? (
        <p className="floor-tray-advice" data-testid="floor-tray-replan">
          {t("inventory.floor.tray.adviceReplan")}
        </p>
      ) : null}

      <div className="floor-tray-actions">
        {advice === "retry" ? (
          <FloorButton onClick={onRetry} variant="primary" testId="floor-tray-retry">
            {t("inventory.floor.tray.retry")}
          </FloorButton>
        ) : null}

        {target.route === "unit" ? (
          <Link
            to="/floor/units/$id"
            params={{ id: target.id }}
            className="floor-go"
            data-testid="floor-tray-open"
          >
            {t("inventory.floor.tray.open")}
          </Link>
        ) : null}
        {target.route === "count" ? (
          <Link
            to="/floor/counts/$id"
            params={{ id: target.id }}
            className="floor-go"
            data-testid="floor-tray-open"
          >
            {t("inventory.floor.tray.open")}
          </Link>
        ) : null}
        {target.route === "transfer" ? (
          <Link
            to="/floor/receive/$id"
            params={{ id: target.id }}
            className="floor-go"
            data-testid="floor-tray-open"
          >
            {t("inventory.floor.tray.open")}
          </Link>
        ) : null}
        {/* 07-A: a parked quality tap points at its INSPECTION. §9.1's recovery
            ("completed by someone else while you were offline") is a decision
            the operator can only take by looking at the inspection, and a tray
            row with no way there is a dead end holding a measurement. */}
        {target.route === "inspection" ? (
          <Link
            to="/floor/quality/$id"
            params={{ id: target.id }}
            className="floor-go"
            data-testid="floor-tray-open"
          >
            {t("inventory.floor.tray.open")}
          </Link>
        ) : null}
        {/* PR-D3 (D-286): a parked shipment tap points at its ORDER's pick
            screen. Spec 09 §9.10's recovery for a refused pick is "open it and
            re-pick with the fresh remaining" — the unit moved, or another
            storekeeper took the metres — and a tray row with no way there
            holds a pick the operator can only discard. */}
        {target.route === "ship" ? (
          <Link
            to="/floor/ship/$orderId"
            params={{ orderId: target.id }}
            className="floor-go"
            data-testid="floor-tray-open"
          >
            {t("inventory.floor.tray.open")}
          </Link>
        ) : null}

        {confirming ? (
          <>
            <FloorButton onClick={onDiscard} variant="danger" testId="floor-tray-discard-confirm">
              {t("inventory.floor.tray.discardConfirm")}
            </FloorButton>
            <FloorButton onClick={onCancelDiscard} variant="ghost">
              {t("inventory.common.cancel")}
            </FloorButton>
          </>
        ) : (
          <FloorButton onClick={onConfirmDiscard} variant="ghost" testId="floor-tray-discard">
            {t("inventory.floor.tray.discard")}
          </FloorButton>
        )}
      </div>

      {confirming ? (
        <p className="floor-tray-warning" role="alert" data-testid="floor-tray-discard-warning">
          {t("inventory.floor.tray.discardWarning")}
        </p>
      ) : null}
    </li>
  );
}

/**
 * One server-conflicted production event (D-080). Names WHAT was tapped (label
 * + tap-time identity), WHEN, and the SERVER's reason in operator words —
 * never the raw `conflict_reason` token. The single action is a confirmed
 * dismiss whose confirmation says the load-bearing thing: the record already
 * sits in the office conflict queue (S7) and dismissing removes only this
 * device's note.
 */
function ReviewRow({
  item,
  confirming,
  onConfirmDismiss,
  onCancelDismiss,
  onDismiss,
}: {
  item: OutboxItem;
  confirming: boolean;
  onConfirmDismiss: () => void;
  onCancelDismiss: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const label = itemLabel(item);
  const reason = item.review?.reason;

  return (
    <li className="floor-tray-row" data-testid="floor-tray-review-row">
      <p className="floor-tray-op" data-testid="floor-tray-review-op">
        {t(label.key, label.params)}
        {item.identity !== undefined ? ` · ${item.identity}` : ""}
      </p>
      <p className="floor-tray-when" data-testid="floor-tray-review-when">
        <TrayWhen at={item.createdAt} />
      </p>

      <p className="floor-tray-reason" data-testid="floor-tray-review-reason">
        {reason === undefined
          ? t("inventory.floor.tray.unknownReason")
          : // The reason table mirrors floor-errors' translated-code pattern:
            // a known reason renders its sentence; an unknown one falls back
            // to a sentence CONTAINING the code rather than the bare token.
            t(`inventory.floor.tray.reviewReasons.${reason}`, {
              defaultValue: t("inventory.floor.tray.reviewReasonFallback", { reason }),
            })}
      </p>

      <div className="floor-tray-actions">
        {confirming ? (
          <>
            <FloorButton onClick={onDismiss} variant="danger" testId="floor-tray-review-confirm">
              {t("inventory.floor.tray.reviewDismissConfirm")}
            </FloorButton>
            <FloorButton onClick={onCancelDismiss} variant="ghost">
              {t("inventory.common.cancel")}
            </FloorButton>
          </>
        ) : (
          <FloorButton
            onClick={onConfirmDismiss}
            variant="ghost"
            testId="floor-tray-review-dismiss"
          >
            {t("inventory.floor.tray.reviewDismiss")}
          </FloorButton>
        )}
      </div>

      {confirming ? (
        <p className="floor-tray-warning" role="alert" data-testid="floor-tray-review-warning">
          {t("inventory.floor.tray.reviewDismissWarning")}
        </p>
      ) : null}
    </li>
  );
}

/** The physical identity of a held item, for the other-user list. */
function TrayIdentity({ item }: { item: OutboxItem }) {
  const { t } = useTranslation();
  const label = itemLabel(item);
  return (
    <span>
      {t(label.key, label.params)}
      {item.identity !== undefined ? ` · ${item.identity}` : ""}
    </span>
  );
}

/** "14 min ago" — reusing the snapshot-age buckets so both read the same way. */
function TrayWhen({ at }: { at: number }) {
  const { t } = useTranslation();
  const age = snapshotAgeParts(Math.max(0, Date.now() - at));
  return <>{t(age.key, { count: age.n })}</>;
}
